import { menuSounds } from '../ui/MenuSounds';

export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export function tryLockLandscape(): void {
  const orientation = (screen as Screen & {
    orientation?: { lock?: (kind: string) => Promise<void> };
  }).orientation;
  if (!orientation || typeof orientation.lock !== 'function') return;
  orientation.lock('landscape').catch(() => { /* not allowed outside fullscreen */ });
}

export function isKeyboardTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

export function setupTouchPauseButton(onTap: () => void): void {
  const btn = document.getElementById('touch-pause');
  if (!btn) return;
  // Use pointer events instead of `click`. After extended multi-touch
  // play, mobile browsers occasionally drop synthesized clicks (gesture
  // recognition, touch-action heuristics, double-tap zoom suppression
  // backing off). Pointer events are immediate and not subject to those
  // heuristics. Pattern: arm on pointerdown, fire on pointerup if the
  // finger is still over the button — so a slide-off cancels.
  let armedPointer: number | null = null;

  const arm = (e: PointerEvent): void => {
    e.preventDefault();
    armedPointer = e.pointerId;
    btn.classList.add('is-pressed');
    try { btn.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const fire = (e: PointerEvent): void => {
    if (armedPointer !== e.pointerId) return;
    armedPointer = null;
    btn.classList.remove('is-pressed');
    try { btn.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    onTap();
  };
  const cancel = (e: PointerEvent): void => {
    if (armedPointer !== e.pointerId && armedPointer !== null) return;
    if (armedPointer !== null) {
      try { btn.releasePointerCapture(armedPointer); } catch { /* ignore */ }
    }
    armedPointer = null;
    btn.classList.remove('is-pressed');
  };

  btn.addEventListener('pointerdown', arm);
  btn.addEventListener('pointerup', fire);
  btn.addEventListener('pointercancel', cancel);
  btn.addEventListener('lostpointercapture', cancel);
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
}

export function primeAudioOnFirstGesture(): void {
  // Mobile Safari + Chrome both keep AudioContexts in `suspended` until
  // a user gesture lets them resume. Most subsystems already handle
  // this lazily, but priming on the first tap means the title menu
  // SFX and the ignition cue have zero perceptible delay.
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown'];
  const fire = (): void => {
    for (const e of events) window.removeEventListener(e, fire, true);
    menuSounds.unlock();
  };
  for (const e of events) window.addEventListener(e, fire, { once: true, capture: true });
}

export function registerServiceWorker(): void {
  // Only register in production builds. In dev Vite serves modules
  // through HMR; intercepting those would break hot reload.
  if (!('serviceWorker' in navigator)) return;
  if (!import.meta.env.PROD) return;
  const register = (): void => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* ignore */ });
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

export function deferVibeJamWidget(): void {
  const load = (): void => {
    if (document.querySelector('script[src="https://vibej.am/2026/widget.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://vibej.am/2026/widget.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const requestIdle = window.requestIdleCallback;
  if (typeof requestIdle === 'function') {
    requestIdle(load, { timeout: 5000 });
  } else {
    window.setTimeout(load, 2500);
  }
}
